import { defineStore, storeToRefs } from 'pinia';
import type { ComputedRef } from 'vue';
import { computed, onMounted, onUnmounted, watch } from 'vue';

import { useLanguageEditor } from '@/composables/useLanguageEditor';
import { useResgate } from '@/login/composables/useResgate';
import { i18n } from '@/i18n';
import { RestManager } from '@/newApi/services/rest/RestManager';
import type { Locale } from '@sysarb/sysarb-types/general/locales';
import { availableLanguages, defaultLanguage, locales } from '@sysarb/sysarb-types/general/locales';
import type { LanguageProgress } from '@sysarb/sysarb-types/services/v1/authentication';
import type { RemovableRef } from '@vueuse/core';
import { StorageSerializers, useLocalStorage, usePreferredLanguages } from '@vueuse/core';
import { useUser } from '@/login/composables/useUser';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { useLoginManager } from '@/login/composables/useLoginManager';
import { useSettings } from '@/login/composables/useSettings';
import type { CustomizationTranslationIdentifier } from '@sysarb/sysarb-types/general/enums/customEmployment';

type LangResult = {
    language: ComputedRef<string>;
    updateLanguage: (lang: string) => Promise<void>;
    systemLanguage: ComputedRef<string>;
    languages: ComputedRef<LocaleWithTranslation[]>;
    getLanguageTranslation: (lang: string) => string;
};

type LangStoreData = {
    _language: RemovableRef<string>;
    _progress: LanguageProgress[];
};

type LocaleWithProgress = Locale & { progress: number };
type LocaleWithTranslation = LocaleWithProgress & { translation: string };

const useLanguageStore = defineStore('languageStore', {
    state: (): LangStoreData => ({
        _language: useLocalStorage<string>('language', defaultLanguage, { serializer: StorageSerializers.string }),
        _progress: []
    }),
    getters: {
        savedLanguage: state => state._language,
        progress: state => state._progress
    },
    actions: {
        setSavedLanguage(lang: string): void {
            this._language = lang;
        },
        setProgress(progress: LanguageProgress[]): void {
            this._progress = progress;
        }
    }
});

export function useLanguage(): LangResult {
    const { selectedInstance } = storeToRefs(useLoginStore());
    const { language: userLanguage, setLanguage, isTranslator } = useUser();
    const { customizationSettings } = useSettings();
    const { loginResgate } = useLoginManager();
    const browserLanguages = usePreferredLanguages();
    const resgate = useResgate(crypto.randomUUID());
    const rest = new RestManager('authentication-service');

    const languageStore = useLanguageStore();
    const { savedLanguage, progress } = storeToRefs(languageStore);

    const systemLanguage = computed<string>(() => selectedInstance.value?.DefaultLang ?? defaultLanguage);

    const languages = computed<LocaleWithTranslation[]>(() => locales.filter(c => availableLanguages.includes(c.code)).map(locale => ({
        ...locale,
        progress: progress.value.find(p => p.language === locale.code)?.progress ?? 0,
        translation: getLanguageTranslation(locale.code)
    })).sort((a, b) => a.name.localeCompare(b.name)));

    const language = computed(() => {
        if (isTranslator === true) {
            return 'ss';
        }

        if (userLanguage.value != null) {
            return userLanguage.value;
        }

        if (savedLanguage.value != null) {
            return savedLanguage.value;
        }

        for (const browserLang of browserLanguages.value) {
            const locale = locales.find(c => c.code === browserLang);

            if (locale != null) {
                return locale.code;
            }
        }

        if (systemLanguage.value != null) {
            return systemLanguage.value;
        }

        return defaultLanguage;
    });

    async function updateLanguage(lang: string): Promise<void> {
        await loadLanguageAndSetLanguage(lang);

        // Stop processing if in translation mode.
        if (lang === 'ss') {
            console.info({
                cat: 'useLanguage',
                msg: 'In translation mode, skipping language update.'
            });
            return;
        }

        languageStore.setSavedLanguage(lang);

        console.info({
            cat: 'useLanguage',
            msg: `Language updated to ➡️ ${lang}`
        });

        if (userLanguage.value != null) {
            void setLanguage(lang);
            void loginResgate(); // Do a resgate login to update the user language. In the auth token.
        }
    }

    async function loadTranslationProgress(): Promise<void> {
        if (progress.value.length > 0) return;

        try {
            const result = await rest.get<LanguageProgress[]>('v1/translation-progress');

            if (result == null) return;
            languageStore.setProgress(result);
        } catch (e: unknown) {
            console.warn({
                cat: 'useLanguage',
                msg: e
            });
        }
    }

    async function loadLanguageAndSetLanguage(lang: string): Promise<void> {
        // load locale messages with dynamic import
        const messages = await import(`@/lang/${lang.toLowerCase()}.json`);
        let localMessage = messages.default;

        if (customizationSettings.value?.customizationTranslationIdentifier != null) {
            const customerCuMessages = await import(`@/lang/custom/${customizationSettings.value.customizationTranslationIdentifier}/cu.json`);
            const customerMessages = await import(`@/lang/custom/${customizationSettings.value.customizationTranslationIdentifier}/${lang.toLowerCase()}.json`);
            // set locale and locale message
            localMessage = { ...messages.default, ...customerMessages.default, cu: customerCuMessages.default };
        }

        i18n.global.setLocaleMessage(lang, localMessage);
        i18n.global.locale.value = lang;
    }

    /**
     * Add a language to the locale messages. used for loading languages in the background without setting them as the current language.
     * @param lang - The language code to load
     */
    async function addLanguageToLocaleMessages(lang: string): Promise<void> {
        const messages = await import(`@/lang/${lang.toLowerCase()}.json`);

        i18n.global.setLocaleMessage(lang, messages.default);
    }

    /**
     * Adds the langague and the custom language to the locale messages. This is used for loading languages in the background without setting them as the current language, but also loading the custom translations if available.
     * @param lang - The language code to load
     * @param customLanguageIdentifier - The custom language identifier
     */
    async function addCustomLanguageToLocaleMessages(lang: string, customLanguageIdentifier: CustomizationTranslationIdentifier | null | undefined): Promise<void> {
        const messages = await import(`@/lang/${lang.toLowerCase()}.json`);
        let localMessage = messages.default;

        if (customLanguageIdentifier != null) {
            const customerCuMessages = await import(`@/lang/custom/${customLanguageIdentifier}/cu.json`);
            const customerMessages = await import(`@/lang/custom/${customLanguageIdentifier}/${lang.toLowerCase()}.json`);
            // set locale and locale message
            localMessage = { ...messages.default, ...customerMessages.default, cu: customerCuMessages.default };
        }

        i18n.global.setLocaleMessage(lang, localMessage);
    }

    function getLanguageTranslation(lang: string): string {
        switch (lang) {
            case 'en':
                return i18n.global.t('system.languages.en');
            case 'sv':
                return i18n.global.t('system.languages.sv');
            case 'nb':
                return i18n.global.t('system.languages.nb');
            case 'es':
                return i18n.global.t('system.languages.es');
            case 'nl':
                return i18n.global.t('system.languages.nl');
            case 'fr':
                return i18n.global.t('system.languages.fr');
            default:
                return '';
        }
    }

    // Check if we should enable in-context translations.
    if (isTranslator === true) {
        // Inject the pseudo language for in-context translations into the available languages.
        if (!availableLanguages.includes('ss')) {
            availableLanguages.push('ss');
        }

        // Inject the pseudo language for in-context translations into the locales.
        if (locales.find(c => c.code === 'ss') == null) {
            locales.push({ code: 'ss', name: 'TRANSLATE', culture: 'ss-ZA' });
            void enableTranslations();
        }
    } else if (language.value === 'ss') {
        void updateLanguage(systemLanguage.value);
    }

    // Enable in-context translations.
    async function enableTranslations(): Promise<void> {
        // The selected language must be the pseudo language set up in Crowdin.
        await updateLanguage('ss');
        // Enable in-context translations.
        void useLanguageEditor().enable(true);
    }

    /**
     * This watcher is used to load additional languages in the background when the language information is available.
     */
    watch([
        (): number | undefined => selectedInstance.value?.DefaultLanguage,
        (): CustomizationTranslationIdentifier | null | undefined => selectedInstance.value?.involveSettings?.customizationTranslationIdentifier
    ], async () => {
        // Default language for the instance needs to be loaded but not applied in the background for mails and other exports or files
        // that needs to be translated in the domains selected language and not by user loaded language.
        if (selectedInstance.value?.DefaultLang != null) {
            await addLanguageToLocaleMessages(selectedInstance.value.DefaultLang);
            await addCustomLanguageToLocaleMessages(selectedInstance.value.DefaultLang, customizationSettings.value?.customizationTranslationIdentifier);
        }

        if (userLanguage.value != null) {
            void addCustomLanguageToLocaleMessages(userLanguage.value, customizationSettings.value?.customizationTranslationIdentifier);
        }
    });

    // Update saved language if it was changed in backend.
    watch(() => userLanguage.value, () => {
        if (userLanguage.value == null) return;

        if (userLanguage.value !== savedLanguage.value) {
            console.info({
                cat: 'useLanguage',
                msg: `User language changed to ${userLanguage.value}`
            });
            void updateLanguage(userLanguage.value);
        }

    }, { immediate: true });

    onMounted(() => {
        void loadTranslationProgress();
    });

    onUnmounted(() => {
        resgate.unsubscribe();
        rest.abort();
    });

    return {
        language,
        updateLanguage,
        systemLanguage,
        languages,
        getLanguageTranslation
    };
}
